import React from 'react';
import SettingsSection from '../../settings-and-privacy/components/SettingsSection';
import SettingItem from '../../settings-and-privacy/components/SettingItem';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const AlertConfigurationTab = ({ settings, onSettingChange, onRootSettingChange, loading }) => {
  const actionTypeOptions = [
    { 
      key: 'dataModifications', 
      label: 'Data Modifications', 
      description: 'Alerts when symptom data is created, updated, or deleted',
      icon: 'Edit'
    },
    { 
      key: 'accessAttempts', 
      label: 'Access Attempts', 
      description: 'Alerts when users view or access symptom data',
      icon: 'Eye'
    },
    { 
      key: 'securityViolations', 
      label: 'Security Violations', 
      description: 'Alerts for unauthorized access attempts or policy violations',
      icon: 'ShieldAlert'
    },
    { 
      key: 'systemEvents', 
      label: 'System Events', 
      description: 'Alerts for system-level events and configuration changes',
      icon: 'Server'
    }
  ];

  const roleOptions = [
    { key: 'admin', label: 'Admin', description: 'System administrators' },
    { key: 'healthcareProfessional', label: 'Healthcare Professional', description: 'Doctors and medical staff' },
    { key: 'caregiver', label: 'Caregiver', description: 'Family caregivers and support staff' },
    { key: 'standardUser', label: 'Standard User', description: 'Regular application users' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Types */}
      <SettingsSection
        title="Notification Categories"
        description="Choose which audit event types trigger notifications"
        icon="Bell"
        collapsible={false}
      >
        <div className="space-y-6">
          {actionTypeOptions?.map((action) => (
            <div key={action?.key} className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 flex-shrink-0">
                  <Icon name={action?.icon} size={20} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <Checkbox
                    checked={settings?.actionTypes?.[action?.key]}
                    onChange={(e) => onSettingChange('actionTypes', action?.key, e?.target?.checked)}
                    label={action?.label}
                    description={action?.description}
                  />
                </div>
              </div>
              
              {settings?.actionTypes?.[action?.key] && (
                <div className="ml-13 pl-4 border-l-2 border-border">
                  <SettingItem
                    label="Severity Threshold"
                    description="Only notify when severity is at or above this level (1-10)"
                    type="slider"
                    value={settings?.severityThresholds?.[action?.key]}
                    onChange={(value) => onSettingChange('severityThresholds', action?.key, value)}
                    onButtonClick={() => {}}
                    icon=""
                    min={1}
                    max={10}
                    step={1}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </SettingsSection>

      {/* Role-Based Routing */}
      <SettingsSection
        title="Role-Based Alert Routing"
        description="Configure which user roles receive specific alert types"
        icon="Users"
        collapsible={true}
        defaultOpen={false}
      >
        <div className="space-y-3">
          {roleOptions?.map((role) => (
            <Checkbox
              key={role?.key}
              checked={settings?.roleBasedRouting?.[role?.key]}
              onChange={(e) => onSettingChange('roleBasedRouting', role?.key, e?.target?.checked)}
              label={role?.label}
              description={role?.description}
            />
          ))}
        </div>
      </SettingsSection>

      {/* Escalation Settings */}
      <SettingsSection
        title="Alert Escalation"
        description="Automatically escalate critical alerts to higher priority"
        icon="TrendingUp"
        collapsible={true}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div className="ml-13 pl-4 border-l-2 border-border">
            <SettingItem
              label="Enable Escalation"
              description="Automatically escalate alerts above threshold to all admin roles"
              type="toggle"
              value={settings?.escalationEnabled}
              onChange={(value) => onRootSettingChange('escalationEnabled', value)}
              onButtonClick={() => {}}
              icon=""
              min={0}
              max={1}
            />
          </div>
          
          {settings?.escalationEnabled && (
            <div className="ml-13 pl-4 border-l-2 border-border">
              <SettingItem
                label="Escalation Threshold"
                description="Severity level that triggers automatic escalation"
                type="slider"
                value={settings?.escalationThreshold}
                onChange={(value) => onRootSettingChange('escalationThreshold', value)}
                onButtonClick={() => {}}
                icon=""
                min={1}
                max={10}
                step={1}
              />
            </div>
          )}
        </div>
      </SettingsSection>

      {/* Preview Section */}
      <div className="bg-muted/50 rounded-xl p-6 border border-border">
        <div className="flex items-start gap-3 mb-4">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">Configuration Summary</h3>
            <p className="text-sm text-muted-foreground">
              {Object.values(settings?.actionTypes || {})?.filter(Boolean)?.length} notification categories enabled
              {settings?.escalationEnabled && ` • Escalation active at severity ${settings?.escalationThreshold}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertConfigurationTab;